import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { FaUser, FaSignOutAlt, FaBars, FaTimes, FaChartBar, FaUsers, FaClipboardList } from 'react-icons/fa';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: var(--surface);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  height: 80px;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    padding: 0 15px;
    justify-content: space-between;
    gap: 10px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  img {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3);
  }

  .logo-text {
    h1 {
      font-size: 1.5rem;
      font-weight: 700;
      color: var(--primary);
      margin: 0;
      line-height: 1.2;
    }

    span {
      font-size: 0.9rem;
      color: var(--text-secondary);
      font-weight: 500;
    }
  }
  
  @media (max-width: 768px) {
    .logo-text {
      h1 {
        font-size: 1.2rem;
      }
      
      span {
        font-size: 0.8rem;
      }
    }
  }
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  gap: 20px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: #333;
  border: 2px solid #fff;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  width: 44px;
  height: 44px;
  position: relative;
  
  &:hover {
    background-color: #555;
    color: #fff;
  }
  
  @media (max-width: 768px) {
    display: flex !important;
    align-items: center;
    justify-content: center;
    z-index: 1001;
    position: relative;
    margin-left: 10px;
  }
`;

const MobileMenu = styled.div`
  display: none;
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  background: var(--surface);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border-top: 1px solid var(--border);
  z-index: 999;
  transform: translateY(-100%);
  transition: transform 0.3s ease;
  opacity: 0;
  visibility: hidden;
  
  &.open {
    transform: translateY(0);
    opacity: 1;
    visibility: visible;
  }
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileNavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  text-decoration: none;
  color: var(--text-primary);
  font-weight: 500;
  border-bottom: 1px solid var(--border);
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--background);
    color: var(--primary);
  }
  
  &.active {
    background-color: var(--primary);
    color: var(--accent);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const MobileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  text-decoration: none;
  color: var(--text-primary);
  font-weight: 500;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  border-bottom: 1px solid var(--border);
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background-color: var(--background);
    color: var(--primary);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  text-decoration: none;
  color: var(--text-primary);
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--background);
    color: #20AC9F;
  }
  
  &.active {
    background-color: #15cfbcff;
    color: white;
    font-weight: 800;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
  
  .user-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background-color: var(--background);
    border-radius: 8px;
    font-weight: 500;
    
    @media (max-width: 768px) {
      padding: 6px 12px;
      font-size: 0.9rem;
    }
    
    .user-icon {
      color: var(--secondary);
    }
  }
  
  .logout-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background-color: #20AC9F;
    color: var(--accent);
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s ease;
    
    @media (max-width: 768px) {
      padding: 6px 12px;
      font-size: 0.9rem;
    }
    
    &:hover {
      background-color: #1A8C7F;
      transform: translateY(-1px);
    }
  }
`;

const Header = () => {
  const { client, admin, logoutClient, logoutAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    if (client) {
      logoutClient();
      navigate('/');
    } else if (admin) {
      logoutAdmin();
      navigate('/');
    }
    setMobileMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Verificar se há usuário logado
  const isLoggedIn = client || admin;

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo>
          <img src="/logo.png" alt="Matheus Barber Logo" />
          <div className="logo-text">
            <h1>Matheus Barber</h1>
            <span>Desde 2018</span>
          </div>
        </Logo>

        {/* Navegação Desktop */}
        <Navigation>
          {client && (
            <NavLink to="/cliente/dashboard" className={isActive('/cliente/dashboard') ? 'active' : ''}>
              <FaUser />
              Meu Perfil
            </NavLink>
          )}
          
          {admin && !admin.is_first_login && (
            <>
              <NavLink to="/admin/dashboard" className={isActive('/admin/dashboard') ? 'active' : ''}>
                Dashboard
              </NavLink>
             
            </>
          )}
        </Navigation>

        {/* Seção do usuário e botão mobile */}
        {isLoggedIn && (
          <>
            <UserSection>
              <div className="user-info">
                <FaUser className="user-icon" />
                {client ? client.name?.split(' ')[0] : (admin?.is_first_login ? 'Configuração Inicial' : admin?.name?.split(' ')[0] || 'Usuário')}
              </div>
              <button className="logout-btn" onClick={handleLogout}>
                <FaSignOutAlt />
                Sair
              </button>
            </UserSection>

            {/* Botão do menu mobile */}
            <MobileMenuButton onClick={toggleMobileMenu} aria-label="Menu">
              {mobileMenuOpen ? <FaTimes /> : <FaBars />}
            </MobileMenuButton>
          </>
        )}
      </HeaderContent>

      {/* Menu mobile */}
      <MobileMenu className={mobileMenuOpen ? 'open' : ''}>
        {client && (
          <MobileNavLink 
            to="/cliente/dashboard" 
            className={isActive('/cliente/dashboard') ? 'active' : ''}
            onClick={closeMobileMenu}
          >
            <FaUser />
            Meu Perfil
          </MobileNavLink>
        )}
        
        {admin && !admin.is_first_login && (
          <>
            <MobileNavLink 
              to="/admin/dashboard" 
              className={isActive('/admin/dashboard') ? 'active' : ''}
              onClick={closeMobileMenu}
            >
              Dashboard
            </MobileNavLink>
            <MobileNavLink 
              to="/admin/clientes" 
              className={isActive('/admin/clientes') ? 'active' : ''}
              onClick={closeMobileMenu}
            >
              <FaUsers />
              Clientes
            </MobileNavLink>
            <MobileNavLink 
              to="/admin/atendimentos" 
              className={isActive('/admin/atendimentos') ? 'active' : ''}
              onClick={closeMobileMenu}
            >
              <FaClipboardList />
              Atendimentos
            </MobileNavLink>
            <MobileNavLink 
              to="/admin/relatorios" 
              className={isActive('/admin/relatorios') ? 'active' : ''}
              onClick={closeMobileMenu}
            >
              <FaChartBar />
              Relatórios
            </MobileNavLink>
          </>
        )}
        
        <MobileButton onClick={handleLogout}>
          <FaSignOutAlt />
          Sair
        </MobileButton>
      </MobileMenu>
    </HeaderContainer>
  );
};

export default Header;
